import { CiqIconBox } from "components/svg-icons";
import { Button, Dropdown } from "antd";
import { type CSSProperties, useContext, useMemo, useState } from "react";
import { ErrorMessages } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import Tooltip from "antd/es/tooltip";
import { FeatureTypes } from "entity-app/constants";
import { useLocation } from "react-router";
import { DownOutlined } from "@ant-design/icons";
import {
  TEditingEntity,
  TEntityLink,
  TFeatureDetail,
  TLinkSource,
  TMaterialLinkDirection
} from "../models";
import FeatureRowCard from "./cards";
import ActivityTrackingCheckbox from "./track-chechbox/activity-tracking-chechbox";
import MaterialToMaterialTrackingCheckbox from "./track-chechbox/material-material-tracking";

type EntityProps = {
  list: TLinkSource[];
  featureDetail: TFeatureDetail;
  isEditing: boolean;
  isDisabled: boolean;
  onEdit: (materialLinkDirection?: TMaterialLinkDirection) => void;
  onCancel: () => void;
  onSave: () => Promise<void>;
  editingEntity: TEditingEntity | null;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
  refetchEntityInfo: () => Promise<void>;
  selectedEntityInfo: TEntityLink;
  featureTypeId: FeatureTypes;
};

type TLinkedFeatureListSections = {
  listPersiste: TLinkSource[];
  listDeleted: TLinkSource[];
};

const getInitialActivityTrackingState = (
  list: TLinkSource[],
  isActivityCheckboxAvailable: boolean,
  source: string | null
): boolean =>
  !!(list.length > 0 || !isActivityCheckboxAvailable || source === "submittal");

const getInitialMaterialTrackingState = (
  list: TLinkSource[],
  isMaterialCheckboxAvailable: boolean,
  source: string | null
): boolean =>
  !!(list.length > 0 || !isMaterialCheckboxAvailable || source === "material");

const getContainerStyle = (
  isCollapsed: boolean,
  isExpanded: boolean,
  isEditing: boolean
): CSSProperties => ({
  ...(isCollapsed && { minHeight: 56, maxHeight: 56 }),
  ...(isExpanded && !isEditing && { minHeight: 0 })
});

const getContainerClassName = (
  isEditing: boolean,
  isExpanded: boolean
): string =>
  `flex flex-col relative w-full px-5 border-0 border-b border-solid border-ciq-gray-950 border-opacity-20 transition-[min-height,max-height] duration-300 ${
    isEditing ? "grow h-full min-h-0" : isExpanded ? "grow min-h-0" : ""
  }`;

function TrackingControls({
  list,
  isEditing,
  isDisabled,
  isReadOnly,
  isActivityCheckboxAvailable,
  isMaterialCheckboxAvailable,
  refetchEntityInfo,
  selectedEntityInfo,
  isActivityTrackingChecked,
  setActivityTrackingChecked,
  isMaterialTrackingChecked,
  setMaterialTrackingChecked
}: {
  list: TLinkSource[];
  isEditing: boolean;
  isDisabled: boolean;
  isReadOnly: boolean;
  isActivityCheckboxAvailable: boolean;
  isMaterialCheckboxAvailable: boolean;
  refetchEntityInfo: () => Promise<void>;
  selectedEntityInfo: TEntityLink;
  isActivityTrackingChecked: boolean;
  setActivityTrackingChecked: React.Dispatch<React.SetStateAction<boolean>>;
  isMaterialTrackingChecked: boolean;
  setMaterialTrackingChecked: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const canShowTrackingControls = !isEditing && !isDisabled;

  return (
    <>
      {canShowTrackingControls && isActivityCheckboxAvailable && (
        <ActivityTrackingCheckbox
          list={list}
          refetchEntityInfo={refetchEntityInfo}
          submittalIds={selectedEntityInfo.ids}
          checked={isActivityTrackingChecked}
          setChecked={setActivityTrackingChecked}
          disabled={isReadOnly}
        />
      )}

      {canShowTrackingControls && isMaterialCheckboxAvailable && (
        <MaterialToMaterialTrackingCheckbox
          list={list}
          refetchEntityInfo={refetchEntityInfo}
          selectedEntityInfo={selectedEntityInfo}
          checked={isMaterialTrackingChecked}
          setChecked={setMaterialTrackingChecked}
          disabled={isReadOnly}
        />
      )}
    </>
  );
}

function EditActionButtons({
  loading,
  isExpanded,
  onCancel,
  onSave,
  setLoading
}: {
  loading: boolean;
  isExpanded: boolean;
  onCancel: () => void;
  onSave: () => Promise<void>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <div className="space-x-2">
      <Button
        onClick={onCancel}
        disabled={loading || !isExpanded}
        data-testid="linkedfeatureitems-button-cancel"
      >
        Cancel
      </Button>
      <Button
        type="primary"
        data-testid="linkedfeatureitems-button-save"
        onClick={async () => {
          setLoading(true);
          await onSave();
          setLoading(false);
        }}
        loading={loading || !isExpanded}
      >
        Save
      </Button>
    </div>
  );
}

function MaterialLinkDirectionLabel({
  title,
  subtitle,
  locked
}: {
  title: string;
  subtitle: string;
  locked: boolean;
}) {
  return (
    <div className={`py-1 min-w-[220px] ${locked ? "cursor-not-allowed" : ""}`}>
      <div
        className={`font-semibold text-sm leading-5 ${
          locked ? "text-black text-opacity-25" : "text-black"
        }`}
      >
        {title}
      </div>
      <div
        className={`text-sm leading-5 ${
          locked ? "text-black text-opacity-25" : "text-black text-opacity-45"
        }`}
      >
        {subtitle}
      </div>
    </div>
  );
}

function materialLinkDirectionTooltip(direction: TMaterialLinkDirection) {
  if (direction === "predecessor") {
    return "Cannot link a Predecessor as this Material already has successors.";
  }
  return "Cannot link a Successor as this Material already has predecessors.";
}

function materialDirectionMenuItem(
  direction: TMaterialLinkDirection,
  locked: boolean
) {
  const title = direction === "predecessor" ? "Predecessors" : "Successors";
  const subtitle =
    direction === "predecessor"
      ? "Materials required to release this material"
      : "Materials dependent on this material";
  const label = (
    <MaterialLinkDirectionLabel
      title={title}
      locked={locked}
      subtitle={subtitle}
    />
  );

  return {
    key: direction,
    label: locked ? (
      <Tooltip title={materialLinkDirectionTooltip(direction)}>
        <span className="block">{label}</span>
      </Tooltip>
    ) : (
      label
    )
  };
}

function AddMaterialLinkButton({
  isDisabled,
  isCollapsed,
  hasLinkedItems,
  lockPredecessor,
  lockSuccessor,
  onSelectDirection
}: {
  isDisabled: boolean;
  isCollapsed: boolean;
  hasLinkedItems: boolean;
  lockPredecessor: boolean;
  lockSuccessor: boolean;
  onSelectDirection: (direction: TMaterialLinkDirection) => void;
}) {
  return (
    <Dropdown
      trigger={["click"]}
      disabled={isDisabled || isCollapsed}
      menu={{
        items: [
          materialDirectionMenuItem("predecessor", lockPredecessor),
          materialDirectionMenuItem("successor", lockSuccessor)
        ],
        onClick: ({ key }) => {
          if (
            (key === "predecessor" && lockPredecessor) ||
            (key === "successor" && lockSuccessor)
          ) {
            return;
          }
          onSelectDirection(key as TMaterialLinkDirection);
        }
      }}
    >
      <Button
        type="primary"
        data-testid="linkedfeatureitems-button-add-edit"
        disabled={isDisabled || isCollapsed}
        icon={<DownOutlined />}
        iconPlacement="end"
      >
        {hasLinkedItems ? "Edit" : "Add"}
      </Button>
    </Dropdown>
  );
}

function AddEditActionButton({
  isDisabled: isEditingOtherEntity,
  isReadOnly,
  isCollapsed,
  isMaterialCheckboxAvailable,
  isMaterialTrackingChecked,
  listPersiste,
  listDeleted,
  selectedEntityInfo,
  onEdit,
  onSelectMaterialLinkDirection
}: {
  isDisabled: boolean;
  isReadOnly: boolean;
  isCollapsed: boolean;
  isMaterialCheckboxAvailable: boolean;
  isMaterialTrackingChecked: boolean;
  listPersiste: TLinkSource[];
  listDeleted: TLinkSource[];
  selectedEntityInfo: TEntityLink;
  onEdit: (materialLinkDirection?: TMaterialLinkDirection) => void;
  onSelectMaterialLinkDirection: (direction: TMaterialLinkDirection) => void;
}) {
  const shouldHideAddEditButton =
    isMaterialCheckboxAvailable && !isMaterialTrackingChecked;
  const hasLinkedItems = listPersiste.length > 0 || listDeleted.length > 0;
  const lockPredecessor =
    (selectedEntityInfo.successor_material_links?.length ?? 0) > 0;
  const lockSuccessor =
    (selectedEntityInfo.predecessor_material_links?.length ?? 0) > 0;
  const isDisabled = isEditingOtherEntity || isReadOnly;
  const tooltipTitle =
    !isReadOnly && isEditingOtherEntity
      ? ErrorMessages.EditingLinkingMessage
      : null;

  return (
    <Tooltip title={tooltipTitle}>
      {shouldHideAddEditButton ? (
        ""
      ) : isMaterialCheckboxAvailable ? (
        <AddMaterialLinkButton
          isDisabled={isDisabled}
          isCollapsed={isCollapsed}
          hasLinkedItems={hasLinkedItems}
          lockPredecessor={lockPredecessor}
          lockSuccessor={lockSuccessor}
          onSelectDirection={onSelectMaterialLinkDirection}
        />
      ) : (
        <Button
          type="primary"
          onClick={() => onEdit()}
          data-testid="linkedfeatureitems-button-add-edit"
          disabled={isDisabled || isCollapsed}
        >
          {hasLinkedItems ? "Edit" : "Add"}
        </Button>
      )}
    </Tooltip>
  );
}

function LinkedFeatureActions({
  isEditing,
  loading,
  isExpanded,
  isDisabled,
  isReadOnly,
  isCollapsed,
  isMaterialCheckboxAvailable,
  isMaterialTrackingChecked,
  listPersiste,
  listDeleted,
  selectedEntityInfo,
  onCancel,
  onSave,
  setLoading,
  onEdit,
  onSelectMaterialLinkDirection
}: {
  isEditing: boolean;
  loading: boolean;
  isExpanded: boolean;
  isDisabled: boolean;
  isReadOnly: boolean;
  isCollapsed: boolean;
  isMaterialCheckboxAvailable: boolean;
  isMaterialTrackingChecked: boolean;
  listPersiste: TLinkSource[];
  listDeleted: TLinkSource[];
  selectedEntityInfo: TEntityLink;
  onCancel: () => void;
  onSave: () => Promise<void>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onEdit: (materialLinkDirection?: TMaterialLinkDirection) => void;
  onSelectMaterialLinkDirection: (direction: TMaterialLinkDirection) => void;
}) {
  if (isEditing) {
    return (
      <EditActionButtons
        loading={loading}
        isExpanded={isExpanded}
        onCancel={onCancel}
        onSave={onSave}
        setLoading={setLoading}
      />
    );
  }

  return (
    <AddEditActionButton
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isCollapsed={isCollapsed}
      isMaterialCheckboxAvailable={isMaterialCheckboxAvailable}
      isMaterialTrackingChecked={isMaterialTrackingChecked}
      listPersiste={listPersiste}
      listDeleted={listDeleted}
      selectedEntityInfo={selectedEntityInfo}
      onEdit={onEdit}
      onSelectMaterialLinkDirection={onSelectMaterialLinkDirection}
    />
  );
}

function LinkedFeatureRows({
  items,
  isEditing,
  editingEntity,
  setEditingEntity,
  featureTypeId,
  selectedEntityInfo,
  hidePredecessorTag = false
}: {
  items: TLinkSource[];
  isEditing: boolean;
  editingEntity: TEditingEntity | null;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
  featureTypeId: FeatureTypes;
  selectedEntityInfo: TEntityLink;
  hidePredecessorTag?: boolean;
}) {
  const isAllowSetting =
    selectedEntityInfo?.feature_type_id === FeatureTypes.MATERIALS;

  return (
    <>
      {items.map((item, index) => (
        <FeatureRowCard
          key={index}
          item={item}
          isEditing={isEditing}
          editingEntity={editingEntity}
          setEditingEntity={setEditingEntity}
          featureTypeId={featureTypeId}
          isAllowSetting={isAllowSetting}
          selectedEntityInfo={selectedEntityInfo}
          hidePredecessorTag={hidePredecessorTag}
        />
      ))}
    </>
  );
}

function materialLinkGroupTitle(
  selectedEntityInfo: TEntityLink,
  editingEntity: TEditingEntity | null
): "Predecessors" | "Successors" | null {
  const hasPredecessors =
    (selectedEntityInfo.predecessor_material_links?.length ?? 0) > 0;
  const hasSuccessors =
    (selectedEntityInfo.successor_material_links?.length ?? 0) > 0;
  const direction = editingEntity?.materialLinkDirection;

  if (hasPredecessors || direction === "predecessor") return "Predecessors";
  if (hasSuccessors || direction === "successor") return "Successors";
  return null;
}

function MaterialLinkGroupLabel({
  listPersiste,
  featureDetail,
  selectedEntityInfo,
  editingEntity
}: {
  listPersiste: TLinkSource[];
  featureDetail: TFeatureDetail;
  selectedEntityInfo: TEntityLink;
  editingEntity: TEditingEntity | null;
}) {
  const isMaterialToMaterial =
    selectedEntityInfo.feature_type_id === FeatureTypes.MATERIALS &&
    featureDetail.featureTypeId === FeatureTypes.MATERIALS;
  const title = isMaterialToMaterial
    ? materialLinkGroupTitle(selectedEntityInfo, editingEntity)
    : null;

  if (!title || listPersiste.length === 0) return null;

  return (
    <div
      data-testid="material-link-group-label"
      className="mb-2 text-sm text-ciq-black-a88"
    >
      {title} ({listPersiste.length})
    </div>
  );
}

function LinkedFeatureContent({
  listPersiste,
  listDeleted,
  featureDetail,
  isCollapsed,
  isEditing,
  editingEntity,
  setEditingEntity,
  featureTypeId,
  selectedEntityInfo
}: TLinkedFeatureListSections & {
  featureDetail: TFeatureDetail;
  isCollapsed: boolean;
  isEditing: boolean;
  editingEntity: TEditingEntity | null;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
  featureTypeId: FeatureTypes;
  selectedEntityInfo: TEntityLink;
}) {
  const hidePredecessorTag =
    selectedEntityInfo.feature_type_id === FeatureTypes.MATERIALS &&
    featureDetail.featureTypeId === FeatureTypes.MATERIALS;

  return (
    <div
      data-testid="linked-feature-items-content"
      className={`w-full ${isCollapsed ? "hidden" : "min-h-0 overflow-auto grow"}`}
    >
      <MaterialLinkGroupLabel
        listPersiste={listPersiste}
        featureDetail={featureDetail}
        selectedEntityInfo={selectedEntityInfo}
        editingEntity={editingEntity}
      />
      {listPersiste.length ? (
        <LinkedFeatureRows
          items={listPersiste}
          isEditing={isEditing}
          editingEntity={editingEntity}
          setEditingEntity={setEditingEntity}
          featureTypeId={featureTypeId}
          selectedEntityInfo={selectedEntityInfo}
          hidePredecessorTag={hidePredecessorTag}
        />
      ) : (
        <div
          data-testid="linked-feature-items-empty-state"
          className="flex w-full justify-center items-center text-center pb-10 pt-4"
        >
          <div data-testid="linked-feature-items-empty-message">
            No {featureDetail.titlePlural} linked
          </div>
        </div>
      )}

      {listDeleted?.length ? (
        <div className="">
          <div className="text-xs text-black text-opacity-60 py-1">
            Deleted {featureDetail.titlePlural}
          </div>
          <LinkedFeatureRows
            items={listDeleted}
            isEditing={isEditing}
            editingEntity={editingEntity}
            setEditingEntity={setEditingEntity}
            featureTypeId={featureTypeId}
            selectedEntityInfo={selectedEntityInfo}
            hidePredecessorTag={hidePredecessorTag}
          />
        </div>
      ) : null}
    </div>
  );
}

function LinkedFeatureItems({
  list,
  featureDetail,
  isEditing,
  isDisabled,
  onEdit,
  onCancel,
  onSave,
  editingEntity,
  setEditingEntity,
  refetchEntityInfo,
  selectedEntityInfo,
  featureTypeId
}: EntityProps) {
  const [loading, setLoading] = useState(false);
  const { isProjectArchived } = useContext(ProjectContext);
  const isReadOnly = !!isProjectArchived;

  const isActivityCheckboxAvailable =
    selectedEntityInfo.feature_type_id === FeatureTypes.SUBMITTALS &&
    featureDetail.featureTypeId === FeatureTypes.ACTIVITIES;

  const isMaterialCheckboxAvailable =
    selectedEntityInfo.feature_type_id === FeatureTypes.MATERIALS &&
    featureDetail.featureTypeId === FeatureTypes.MATERIALS;

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const source = urlParams.get("source");

  const [isActivityTrackingChecked, setActivityTrackingChecked] = useState(
    getInitialActivityTrackingState(list, isActivityCheckboxAvailable, source)
  );

  const [isMaterialTrackingChecked, setMaterialTrackingChecked] = useState(
    getInitialMaterialTrackingState(list, isMaterialCheckboxAvailable, source)
  );

  const [materialLinkDirection, setMaterialLinkDirection] =
    useState<TMaterialLinkDirection | null>(null);

  const onSelectMaterialLinkDirection = (direction: TMaterialLinkDirection) => {
    setMaterialLinkDirection(direction);
    onEdit(direction);
  };

  const isCollapsed =
    isDisabled || !isActivityTrackingChecked || !isMaterialTrackingChecked;
  const isExpanded = isActivityTrackingChecked && isMaterialTrackingChecked;

  const { listPersiste, listDeleted } = useMemo(() => {
    return {
      listPersiste: list.filter(
        (x) => !x.feature_instance?.deleted_from_source
      ),
      listDeleted: list.filter((x) => x.feature_instance?.deleted_from_source)
    };
  }, [list]);

  return (
    <div
      style={getContainerStyle(isCollapsed, isExpanded, isEditing)}
      className={getContainerClassName(isEditing, isExpanded)}
      data-material-link-direction={materialLinkDirection ?? undefined}
    >
      <div className="flex w-full justify-between px-1.5 py-2.5">
        <h4
          data-testid="linked-feature-items-title"
          className="flex items-center space-x-1 font-semibold"
        >
          <CiqIconBox feature={featureDetail.feature} />
          <div className="pl-1">Associated {featureDetail.titlePlural}</div>
          <span data-testid="linked-feature-items-count" className="">
            {listPersiste.length > 0 ? ` (${listPersiste.length})` : ""}
          </span>
        </h4>
        <div className="flex items-center space-x-1">
          <TrackingControls
            list={list}
            isEditing={isEditing}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isActivityCheckboxAvailable={isActivityCheckboxAvailable}
            isMaterialCheckboxAvailable={isMaterialCheckboxAvailable}
            refetchEntityInfo={refetchEntityInfo}
            selectedEntityInfo={selectedEntityInfo}
            isActivityTrackingChecked={isActivityTrackingChecked}
            setActivityTrackingChecked={setActivityTrackingChecked}
            isMaterialTrackingChecked={isMaterialTrackingChecked}
            setMaterialTrackingChecked={setMaterialTrackingChecked}
          />

          <LinkedFeatureActions
            isEditing={isEditing}
            loading={loading}
            isExpanded={isExpanded}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isCollapsed={isCollapsed}
            isMaterialCheckboxAvailable={isMaterialCheckboxAvailable}
            isMaterialTrackingChecked={isMaterialTrackingChecked}
            listPersiste={listPersiste}
            listDeleted={listDeleted}
            selectedEntityInfo={selectedEntityInfo}
            onCancel={onCancel}
            onSave={onSave}
            setLoading={setLoading}
            onEdit={onEdit}
            onSelectMaterialLinkDirection={onSelectMaterialLinkDirection}
          />
        </div>
      </div>
      <LinkedFeatureContent
        listPersiste={listPersiste}
        listDeleted={listDeleted}
        featureDetail={featureDetail}
        isCollapsed={isCollapsed}
        isEditing={isEditing}
        editingEntity={editingEntity}
        setEditingEntity={setEditingEntity}
        featureTypeId={featureTypeId}
        selectedEntityInfo={selectedEntityInfo}
      />
    </div>
  );
}

export default LinkedFeatureItems;
